import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  Divider,
  Alert,
  InputAdornment,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import productService from "../../../services/productService";
import purchaseOrderService from "../../../services/purchaseOrderService";
import storeService from "../../../services/storeService";
import { useStore } from "../../../context/StoreContext";
import { useCurrency } from '../../../hooks/useCurrency';

const PurchaseOrderForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const { t } = useTranslation();
  // One system-wide currency (Paramètres › Devise & Système): shown read-only, never picked per PO.
  const { currency: systemCurrency, currencySymbol, format: fmtMoney } = useCurrency();
  const { user } = useSelector((state) => state.auth);
  // The globally-selected store (top-nav "Magasin actuel" switcher) — used
  // below to auto-fill this form's own store picker for a SUPER_ADMIN, who
  // otherwise starts with storeId="" and never gets one on their own (see
  // the effect right after formData's declaration).
  const { currentStore } = useStore();

  // ── Edit-by-status ──────────────────────────────────────
  // DRAFT = fully editable. SENT = notes only, items/header locked.
  // RECEIVED/CANCELLED = read only (this page shouldn't normally be
  // reached for these — PurchaseOrderDetails only links here for
  // DRAFT/SENT — but guard anyway for a direct URL visit).
  const [poStatus, setPoStatus] = useState(isEditMode ? null : "DRAFT");
  const notesOnlyMode = isEditMode && poStatus === "SENT";
  const readOnlyMode =
    isEditMode && (poStatus === "RECEIVED" || poStatus === "CANCELLED");
  const fieldsLocked = notesOnlyMode || readOnlyMode;

  // Check if user is SUPER_ADMIN without store context
  const isSuperAdminWithoutStore =
    user?.globalRole === "SUPER_ADMIN" && !user?.store;

  // ── Form state ──────────────────────────────────────────
  const [formData, setFormData] = useState({
    storeId: "",
    supplierId: "",
    expectedDeliveryDate: "",
    notes: "",
    items: [],
  });

  // ── Dropdown data ───────────────────────────────────────
  const [stores, setStores] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [storeAttributes, setStoreAttributes] = useState([]);

  // ── Per-item state (variants fetched per product) ──────
  // itemMeta[i] = { existingVariants: [...] }
  const [itemMeta, setItemMeta] = useState([]);

  // ── Loading ─────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [pendingPO, setPendingPO] = useState(null);
  const [loadedForEdit, setLoadedForEdit] = useState(false);

  // Auto-fill this form's store picker from the globally-selected store —
  // without this, a SUPER_ADMIN's own picker here started blank even when
  // they already had a store selected via the top-nav switcher, and
  // fetchSuppliers/fetchProducts/fetchAttributes below all wait on this
  // field before doing anything (never firing a single request until the
  // user manually re-picked a store in THIS form too). Skipped in edit
  // mode — there, the PO's own (fixed, non-editable) store wins instead;
  // see the "load the PO's header fields" effect further down.
  useEffect(() => {
    if (!isEditMode && isSuperAdminWithoutStore && currentStore?.id && !formData.storeId) {
      setFormData((prev) => ({ ...prev, storeId: currentStore.id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStore, isEditMode, isSuperAdminWithoutStore]);

  useEffect(() => {
    if (isSuperAdminWithoutStore) {
      fetchStores();
    }

    fetchSuppliers();
    fetchProducts();
    fetchAttributes();
  }, [isSuperAdminWithoutStore]);

  // ── Edit mode: load the PO's header fields first (this also gives us
  // its storeId, which the effect below uses to fetch that store's
  // suppliers/products/attributes for a SUPER_ADMIN) ────────────────
  useEffect(() => {
    if (!isEditMode) return;

    (async () => {
      try {
        const po = await purchaseOrderService.getPurchaseOrderById(id);
        setPoStatus(po.status);
        setFormData((prev) => ({
          ...prev,
          storeId: po.storeId || "",
          supplierId: po.supplierId || "",
          expectedDeliveryDate: po.expectedDeliveryDate || "",
          notes: po.notes || "",
        }));
        setPendingPO(po);
      } catch (err) {
        console.error("Failed to load purchase order:", err);
        toast.error(t("purchaseOrders.fetchError"));
        setInitialLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, id]);

  // ── Edit mode: once the product list is loaded, reconstruct each line
  // item (its `product` object + existing-variant options) from the PO
  // fetched above. Runs once. ─────────────────────────────────────────
  useEffect(() => {
    if (!isEditMode || loadedForEdit || !pendingPO || products.length === 0) return;

    (async () => {
      const items = pendingPO.items.map((item) => ({
        productId: item.productId,
        product: products.find((p) => String(p.id) === String(item.productId)) || null,
        variantId: item.variantId,
        createNewVariant: false,
        attributeValueIds: [],
        quantity: item.quantityOrdered,
        unitCost: item.unitCost,
        sellPrice: item.sellPrice,
      }));

      setFormData((prev) => ({ ...prev, items }));

      const metas = await Promise.all(
        pendingPO.items.map(async (item) => {
          try {
            const variants = await productService.getProductVariants(item.productId);
            return {
              existingVariants: (variants || []).filter(
                (v) => v.isActive !== false || String(v.id) === String(item.variantId),
              ),
              availableValuesByAttribute: {},
            };
          } catch (err) {
            console.error("Failed to fetch variants for edit:", err);
            return { existingVariants: [], availableValuesByAttribute: {} };
          }
        }),
      );
      setItemMeta(metas);

      setLoadedForEdit(true);
      setInitialLoading(false);
    })();
  }, [isEditMode, loadedForEdit, pendingPO, products]);

  // Re-fetch products, suppliers, and attributes when store changes
  // for SUPER_ADMIN
  useEffect(() => {
    if (isSuperAdminWithoutStore && formData.storeId) {
      fetchSuppliers();
      fetchProducts();
      fetchAttributes();
    }
  }, [formData.storeId]);

  const fetchStores = async () => {
    try {
      const data = await storeService.getAllStores();
      setStores(data || []);
    } catch (err) {
      console.error("Failed to fetch stores:", err);
      toast.error(t("stores.fetchError"));
    }
  };

  const fetchSuppliers = async () => {
    try {
      // Unlike products/attributes below, GET /admin/suppliers never reads
      // a storeId at all (SupplierController#getAllSuppliers takes none) —
      // for a SUPER_ADMIN with no home store, the backend already returns
      // every supplier unconditionally. Gating this on formData.storeId
      // (this form's own store picker) served no purpose and just meant
      // the list never loaded until that picker was filled in.
      const data = await purchaseOrderService.getSuppliers();
      // Keep every supplier (not just active ones) — an inactive supplier
      // must stay visible so an existing PO already assigned to it still
      // shows correctly when edited (filtering it out entirely made the
      // field render blank, as if no supplier were assigned at all). It's
      // just not selectable — see the disabled state below.
      setSuppliers(data || []);
    } catch (err) {
      console.error("Failed to fetch suppliers:", err);
    }
  };

  const fetchProducts = async () => {
    try {
      // For SUPER_ADMIN without store, only fetch if storeId is selected
      if (isSuperAdminWithoutStore && !formData.storeId) {
        setProducts([]);
        return;
      }

      const data = await productService.getActiveProducts();
      console.log("data");
      console.log(data);
      setProducts(data || []);
    } catch (err) {
      console.error("Failed to fetch products:", err);
    }
  };

  const fetchAttributes = async () => {
    try {
      // For SUPER_ADMIN without store, only fetch if storeId is selected
      if (isSuperAdminWithoutStore && !formData.storeId) {
        setStoreAttributes([]);
        return;
      }

      // Inactive attributes aren't offered when creating variants
      const attributes = (await productService.getAllAttributes()).filter((a) => a.isActive !== false);

      // Fetch values for each attribute
      const attributesWithValues = await Promise.all(
        attributes.map(async (attr) => {
          try {
            const values = await productService.getAttributeValues(attr.id);

            return {
              ...attr,
              values: values || [],
            };
          } catch (err) {
            console.error(
              `Failed to fetch values for attribute ${attr.id}:`,
              err,
            );

            return {
              ...attr,
              values: [],
            };
          }
        }),
      );

      setStoreAttributes(attributesWithValues || []);
    } catch (err) {
      console.error("Failed to fetch attributes:", err);
      toast.error(t("attributes.fetchError"));
    }
  };

  // ============================================================
  // DUPLICATE VARIANT HELPERS
  // ============================================================

  const getDuplicateVariantMessage = (itemIndex) => {
    const duplicateIndex = findDuplicateVariantIndex(itemIndex);

    if (duplicateIndex === -1) {
      return null;
    }

    const item = formData.items[itemIndex];
    const duplicateItem = formData.items[duplicateIndex];

    const itemProductName = item?.product?.name || `Item ${itemIndex + 1}`;

    const duplicateProductName =
      duplicateItem?.product?.name || `Item ${duplicateIndex + 1}`;

    const getAttributeLabels = (currentItem) => {
      return storeAttributes
        .flatMap((attr) =>
          (attr.values || [])
            .filter((value) =>
              (currentItem?.attributeValueIds || []).some(
                (selectedId) => String(selectedId) === String(value.id),
              ),
            )
            .map((value) => value.displayValue || value.value),
        )
        .join(" / ");
    };

    const variantLabel = getAttributeLabels(item);

    return {
      currentItemNumber: itemIndex + 1,
      duplicateItemNumber: duplicateIndex + 1,
      currentProductName: itemProductName,
      duplicateProductName,
      variantLabel,
    };
  };
  /**
   * Normalize attribute-value IDs so this works with numeric IDs,
   * string IDs, UUIDs, etc. Order does not matter.
   */
  const normalizeIds = (ids = []) =>
    [...new Set((ids || []).map((id) => String(id)))].sort();

  /**
   * Compare two variant combinations regardless of attribute order.
   */
  const isSameVariantCombination = (ids1 = [], ids2 = []) => {
    const a = normalizeIds(ids1);
    const b = normalizeIds(ids2);

    return a.length === b.length && a.every((id, index) => id === b[index]);
  };

  /**
   * Returns true when the item contains one selected value for every
   * store attribute. Since each attribute is a single-select, this is
   * the point at which a complete variant combination exists.
   */
  const isCompleteVariantCombination = (item) => {
    if (!item?.productId || !storeAttributes.length) {
      return false;
    }

    const selectedIds = new Set(
      (item.attributeValueIds || []).map((id) => String(id)),
    );

    return storeAttributes.every((attr) =>
      (attr.values || []).some((value) => selectedIds.has(String(value.id))),
    );
  };

  /**
   * Check whether a complete new-variant combination already exists
   * in another item of this purchase order.
   *
   * Only items for the same product are compared.
   */
  const findDuplicateVariantIndex = (itemIndex) => {
    const item = formData.items[itemIndex];

    if (
      !item?.productId ||
      !item?.attributeValueIds?.length ||
      !isCompleteVariantCombination(item)
    ) {
      return -1;
    }

    return formData.items.findIndex((otherItem, otherIndex) => {
      if (otherIndex === itemIndex) {
        return false;
      }

      if (!otherItem?.productId) {
        return false;
      }

      if (String(otherItem.productId) !== String(item.productId)) {
        return false;
      }

      const otherHasExistingVariants =
        itemMeta[otherIndex]?.existingVariants?.length > 0;

      const otherIsCreatingNew =
        otherItem.createNewVariant || !otherHasExistingVariants;

      if (!otherIsCreatingNew) {
        return false;
      }

      if (!isCompleteVariantCombination(otherItem)) {
        return false;
      }

      return isSameVariantCombination(
        item.attributeValueIds,
        otherItem.attributeValueIds,
      );
    });
  };

  /**
   * Check whether the EXISTING variant selected for this item (not a new
   * one being created) is already selected on another item that is also
   * using the "existing variant" mode. Complements
   * `findDuplicateVariantIndex`, which only covers newly-created variants.
   */
  const findDuplicateExistingVariantIndex = (itemIndex) => {
    const item = formData.items[itemIndex];

    const hasExistingVariants =
      itemMeta[itemIndex]?.existingVariants?.length > 0;
    const isCreatingNew = item?.createNewVariant || !hasExistingVariants;

    if (isCreatingNew || !item?.variantId) {
      return -1;
    }

    return formData.items.findIndex((otherItem, otherIndex) => {
      if (otherIndex === itemIndex) {
        return false;
      }

      if (!otherItem?.variantId) {
        return false;
      }

      const otherHasExistingVariants =
        itemMeta[otherIndex]?.existingVariants?.length > 0;
      const otherIsCreatingNew =
        otherItem.createNewVariant || !otherHasExistingVariants;

      if (otherIsCreatingNew) {
        return false;
      }

      return String(otherItem.variantId) === String(item.variantId);
    });
  };

  /**
   * Disable an existing-variant dropdown option when it is already
   * selected (existing-variant mode) on another item of this PO.
   */
  const isExistingVariantOptionDisabled = (itemIndex, variantId) =>
    formData.items.some((otherItem, otherIndex) => {
      if (otherIndex === itemIndex) {
        return false;
      }

      if (!otherItem?.variantId) {
        return false;
      }

      const otherHasExistingVariants =
        itemMeta[otherIndex]?.existingVariants?.length > 0;
      const otherIsCreatingNew =
        otherItem.createNewVariant || !otherHasExistingVariants;

      if (otherIsCreatingNew) {
        return false;
      }

      return String(otherItem.variantId) === String(variantId);
    });

  /**
   * IMPORTANT:
   * Disable an attribute option only when selecting that option would
   * complete a combination that is already used by another item.
   *
   * Example:
   *   Item 1 = Red + Large
   *   Item 2 = Red + ...
   *
   * While editing Item 2:
   *   - Red stays enabled in the Color selector.
   *   - Large is disabled in the Size selector once Red is selected.
   *
   * This prevents valid partial combinations from being blocked.
   */
  const isAttributeOptionDisabled = (itemIndex, attributeId, optionId) => {
    const item = formData.items[itemIndex];

    if (!item?.productId || optionId == null) {
      return false;
    }

    const hasExistingVariants =
      itemMeta[itemIndex]?.existingVariants?.length > 0;

    const isCreatingNew = item.createNewVariant || !hasExistingVariants;

    if (!isCreatingNew) {
      return false;
    }

    const currentAttribute = storeAttributes.find(
      (attr) => String(attr.id) === String(attributeId),
    );

    if (!currentAttribute) {
      return false;
    }

    const currentAttributeValueIds = new Set(
      (currentAttribute.values || []).map((value) => String(value.id)),
    );

    // Keep the selections belonging to all OTHER attributes.
    const otherAttributeValueIds = (item.attributeValueIds || []).filter(
      (valueId) => !currentAttributeValueIds.has(String(valueId)),
    );

    // This is the combination that would exist if the user selected
    // this option right now.
    const candidateIds = [...otherAttributeValueIds, optionId];

    // Do not disable anything while the current variant is still
    // incomplete. A partial combination can still become valid.
    const candidateItem = {
      ...item,
      attributeValueIds: candidateIds,
    };

    if (!isCompleteVariantCombination(candidateItem)) {
      return false;
    }

    return formData.items.some((otherItem, otherIndex) => {
      if (otherIndex === itemIndex) {
        return false;
      }

      if (!otherItem?.productId) {
        return false;
      }

      if (String(otherItem.productId) !== String(item.productId)) {
        return false;
      }

      const otherHasExistingVariants =
        itemMeta[otherIndex]?.existingVariants?.length > 0;

      const otherIsCreatingNew =
        otherItem.createNewVariant || !otherHasExistingVariants;

      if (!otherIsCreatingNew) {
        return false;
      }

      if (!isCompleteVariantCombination(otherItem)) {
        return false;
      }

      return isSameVariantCombination(
        candidateIds,
        otherItem.attributeValueIds,
      );
    });
  };

  /**
   * Check whether ANY item duplicates another — either a new-variant
   * combination or an existing-variant selection.
   */
  const hasDuplicateVariants = formData.items.some((item, index) => {
    const hasExistingVariants = itemMeta[index]?.existingVariants?.length > 0;

    const isCreatingNew = item.createNewVariant || !hasExistingVariants;

    if (isCreatingNew) {
      return findDuplicateVariantIndex(index) !== -1;
    }

    return findDuplicateExistingVariantIndex(index) !== -1;
  });

  // ============================================================
  // ITEM MANAGEMENT
  // ============================================================

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          productId: null,
          product: null,
          variantId: null,
          createNewVariant: false,
          attributeValueIds: [],
          quantity: 1,
          unitCost: "",
          sellPrice: "",
        },
      ],
    }));

    setItemMeta((prev) => [
      ...prev,
      {
        existingVariants: [],
      },
    ]);
  };

  const removeItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));

    setItemMeta((prev) => prev.filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    setFormData((prev) => {
      const items = [...prev.items];

      items[index] = {
        ...items[index],
        [field]: value,
      };

      return {
        ...prev,
        items,
      };
    });
  };

  // ── Product selected → fetch its variants ──
  const handleProductSelect = async (index, product) => {
    if (!product) {
      // Clear
      setFormData((prev) => {
        const items = [...prev.items];

        items[index] = {
          ...items[index],
          product: null,
          productId: null,
          unitCost: "",
          sellPrice: "",
          variantId: null,
          createNewVariant: false,
          attributeValueIds: [],
        };

        return {
          ...prev,
          items,
        };
      });

      setItemMeta((prev) => {
        const m = [...prev];

        m[index] = {
          existingVariants: [],
        };

        return m;
      });

      return;
    }

    // Set product
    setFormData((prev) => {
      const items = [...prev.items];

      items[index] = {
        ...items[index],
        product,
        productId: product.id,
        variantId: null,
        createNewVariant: false,
        attributeValueIds: [],
      };

      return {
        ...prev,
        items,
      };
    });

    // Fetch existing variants for this product
    try {
      const variants = await productService.getProductVariants(product.id);

      setItemMeta((prev) => {
        const m = [...prev];

        m[index] = {
          // Inactive variants aren't selectable in the PO variant selector
          existingVariants: (variants || []).filter((v) => v.isActive !== false),
          availableValuesByAttribute: {},
        };

        return m;
      });
    } catch (err) {
      console.error("Failed to fetch variants:", err);

      setItemMeta((prev) => {
        const m = [...prev];

        m[index] = {
          existingVariants: [],
          availableValuesByAttribute: {},
        };

        return m;
      });
    }

    // Fetch, per store attribute, which values are NOT already used by an
    // active variant of this product — so the "create new variant" dropdown
    // can't offer a combination that already exists.
    fetchAvailableAttributeValues(index, product.id);
  };

  const fetchAvailableAttributeValues = async (index, productId) => {
    try {
      const results = await Promise.all(
        storeAttributes.map(async (attr) => {
          try {
            const values = await productService.getAvailableAttributeValues(productId, attr.id);
            return [attr.id, values || []];
          } catch (err) {
            console.error(`Failed to fetch available values for attribute ${attr.id}:`, err);
            // Fail open — fall back to all values for this attribute rather
            // than blocking the whole "new variant" flow.
            return [attr.id, attr.values || []];
          }
        }),
      );

      setItemMeta((prev) => {
        const m = [...prev];
        if (!m[index]) return prev;
        m[index] = {
          ...m[index],
          availableValuesByAttribute: Object.fromEntries(results),
        };
        return m;
      });
    } catch (err) {
      console.error("Failed to fetch available attribute values:", err);
    }
  };

  // ── Toggle between existing / new variant ───────────────
  const handleVariantModeChange = (index, mode) => {
    setFormData((prev) => {
      const items = [...prev.items];

      items[index] = {
        ...items[index],
        createNewVariant: mode === "new",
        variantId: mode === "new" ? null : items[index].variantId,
        attributeValueIds: [],
      };

      return {
        ...prev,
        items,
      };
    });
  };

  // ── Attribute value change for new variant ──────────────
  // `selectedValue` is a single attribute-value object (or null when cleared) —
  // a variant can only have ONE value per attribute (e.g. one Color, one Size),
  // so this always replaces this attribute's previous value rather than adding
  // to a set of them.
  const handleAttributeValueChange = (index, attributeId, selectedValue) => {
    setFormData((prev) => {
      const items = [...prev.items];
      const item = items[index];

      if (!item) {
        return prev;
      }

      const attribute = storeAttributes.find(
        (attr) => String(attr.id) === String(attributeId),
      );

      if (!attribute) {
        return prev;
      }

      const attributeValueIds = new Set(
        (attribute.values || []).map((value) => String(value.id)),
      );

      // Remove the previous value belonging to this attribute,
      // then add the newly selected value (if any).
      const kept = (item.attributeValueIds || []).filter(
        (valueId) => !attributeValueIds.has(String(valueId)),
      );

      items[index] = {
        ...item,
        attributeValueIds: selectedValue ? [...kept, selectedValue.id] : kept,
      };

      return {
        ...prev,
        items,
      };
    });
  };

  // ============================================================
  // CALCULATIONS & FORMATTING
  // ============================================================

  const getLineTotal = (item) => {
    return (parseFloat(item.unitCost) || 0) * (parseInt(item.quantity) || 0);
  };

  const getMargin = (item) => {
    const sell = parseFloat(item.sellPrice) || 0;
    const cost = parseFloat(item.unitCost) || 0;

    return sell - cost;
  };

  const getGrandTotal = () => {
    return formData.items.reduce((sum, item) => sum + getLineTotal(item), 0);
  };

  const fmt = (price) => fmtMoney(price);

  // Preview: what the new variant will look like
  const getVariantPreview = (item) => {
    if (!item.product || item.attributeValueIds.length === 0) {
      return null;
    }

    const attrLabels = storeAttributes.flatMap((attr) =>
      (attr.values || [])
        .filter((v) => item.attributeValueIds.includes(v.id))
        .map((v) => v.displayValue || v.value),
    );

    const sellPrice = parseFloat(item.sellPrice) || 0;
    const costPrice = parseFloat(item.unitCost) || 0;
    const margin = sellPrice - costPrice;

    return {
      attrLabels,
      sellPrice,
      costPrice,
      margin,
    };
  };

  // ============================================================
  // SUBMIT
  // ============================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (readOnlyMode) {
      return; // shouldn't be reachable — submit button is hidden/disabled
    }

    // ── SENT: notes-only edit — skip every item/header validation below ──
    if (notesOnlyMode) {
      setLoading(true);
      try {
        await purchaseOrderService.updatePurchaseOrder(id, { notes: formData.notes || null });
        toast.success(t("purchaseOrders.updateSuccess"));
        navigate(`/purchase-orders/${id}`);
      } catch (err) {
        toast.error(err.response?.data?.message || t("common.saveError"));
        console.error(err);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (isSuperAdminWithoutStore && !formData.storeId) {
      toast.error(t("stores.store") + " " + t("common.save"));
      return;
    }

    if (formData.items.length === 0) {
      toast.error(t("purchaseOrders.addItem"));
      return;
    }

    // Validate each item
    for (let i = 0; i < formData.items.length; i++) {
      const item = formData.items[i];

      if (!item.productId) {
        toast.error(
          t("purchaseOrders_ext.selectProductError", {
            number: i + 1,
          }),
        );
        return;
      }

      const hasExisting = itemMeta[i]?.existingVariants?.length > 0;

      if (hasExisting && !item.createNewVariant && !item.variantId) {
        toast.error(
          t("purchaseOrders_ext.selectVariantError", {
            number: i + 1,
          }),
        );
        return;
      }

      if (item.createNewVariant && item.attributeValueIds.length === 0) {
        toast.error(
          t("purchaseOrders_ext.selectAttributesError", {
            number: i + 1,
          }),
        );
        return;
      }

      if (!hasExisting && item.attributeValueIds.length === 0) {
        toast.error(
          t("purchaseOrders_ext.defineAttributesError", {
            number: i + 1,
          }),
        );
        return;
      }

      // Duplicate variant validation
      const isCreatingNew = item.createNewVariant || !hasExisting;

      if (isCreatingNew && isCompleteVariantCombination(item)) {
        const duplicateIndex = findDuplicateVariantIndex(i);

        if (duplicateIndex !== -1) {
          toast.error(
            t("purchaseOrders_ext.duplicateItemError", {
              number: i + 1,
              otherNumber: duplicateIndex + 1,
            }),
          );
          return;
        }
      }
    }

    setLoading(true);

    try {
      const payload = {
        supplierId: formData.supplierId,
        expectedDeliveryDate: formData.expectedDeliveryDate || null,
        currencyCode: systemCurrency,
        notes: formData.notes || null,

        items: formData.items.map((item, itemIndex) => {
          const isNew =
            item.createNewVariant ||
            itemMeta[itemIndex]?.existingVariants?.length === 0;

          return {
            productId: item.productId,
            variantId: isNew ? null : item.variantId,
            attributeValueIds: isNew ? item.attributeValueIds : null,
            quantity: parseInt(item.quantity),
            unitCost: parseFloat(item.unitCost),
            sellPrice: parseFloat(item.sellPrice),
          };
        }),
      };

      // Add storeId for SUPER_ADMIN without store context
      if (isSuperAdminWithoutStore && formData.storeId) {
        payload.storeId = formData.storeId;
      }

      if (isEditMode) {
        await purchaseOrderService.updatePurchaseOrder(id, payload);
        toast.success(t("purchaseOrders.updateSuccess"));
        navigate(`/purchase-orders/${id}`);
      } else {
        await purchaseOrderService.createPurchaseOrder(payload);
        toast.success(t("purchaseOrders.createSuccess"));
        navigate("/purchase-orders");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || t("common.saveError"));

      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // RENDER
  // ============================================================

  if (initialLoading) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography>{t("common.loading")}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
          gap: 2,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() =>
            navigate(isEditMode ? `/purchase-orders/${id}` : "/purchase-orders")
          }
        >
          {t("common.back")}
        </Button>

        <Typography variant="h4">
          {isEditMode
            ? t("purchaseOrders_ext.editPO")
            : t("purchaseOrders.createPO")}
        </Typography>

        {readOnlyMode && (
          <Alert severity="info" sx={{ py: 0 }}>
            {t("purchaseOrders_ext.readOnlyStatus")}
          </Alert>
        )}
        {notesOnlyMode && (
          <Alert severity="info" sx={{ py: 0 }}>
            {t("purchaseOrders_ext.notesOnlyStatus")}
          </Alert>
        )}
      </Box>

      <form onSubmit={handleSubmit}>
        {/* ── PO Header ───────────────────────────────────── */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t("purchaseOrders.viewPO")}
            </Typography>

            <Grid container spacing={3}>
              {/* Store Selector */}
              {isSuperAdminWithoutStore && (
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth required disabled={fieldsLocked || isEditMode}>
                    <InputLabel>{t("roles.store")}</InputLabel>

                    <Select
                      value={formData.storeId}
                      label={t("roles.store")}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          storeId: e.target.value,
                        })
                      }
                    >
                      {stores.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.name} ({s.code})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12} md={isSuperAdminWithoutStore ? 4 : 4}>
                <FormControl fullWidth required disabled={fieldsLocked}>
                  <InputLabel>{t("purchaseOrders.supplier")}</InputLabel>

                  <Select
                    value={formData.supplierId}
                    label={t("purchaseOrders.supplier")}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        supplierId: e.target.value,
                      })
                    }
                  >
                    {suppliers.map((s) => (
                      <MenuItem
                        key={s.id}
                        value={s.id}
                        disabled={s.isActive === false && s.id !== formData.supplierId}
                      >
                        {s.name}
                        {s.isActive === false ? ` (${t("common.inactive")})` : ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t("purchaseOrders.expectedDate")}
                  type="date"
                  value={formData.expectedDeliveryDate}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      expectedDeliveryDate: e.target.value,
                    })
                  }
                  disabled={fieldsLocked}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  disabled
                  label={t("common.currency")}
                  value={`${systemCurrency} (${currencySymbol})`}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("common.notes")}
                  multiline
                  rows={2}
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      notes: e.target.value,
                    })
                  }
                  disabled={readOnlyMode}
                  helperText={
                    notesOnlyMode
                      ? t("purchaseOrders_ext.notesOnlyHelper")
                      : undefined
                  }
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* ── Items ───────────────────────────────────────── */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6">{t("purchaseOrders.items")}</Typography>

              {!fieldsLocked && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={addItem}
                  size="small"
                >
                  {t("purchaseOrders.addItem")}
                </Button>
              )}
            </Box>

            {formData.items.length === 0 && (
              <Alert severity="info">
                {t("purchaseOrders_ext.addItemHint")}
              </Alert>
            )}

            {formData.items.map((item, index) => {
              const hasExistingVariants =
                itemMeta[index]?.existingVariants?.length > 0;

              const isCreatingNew =
                item.createNewVariant || !hasExistingVariants;

              const preview = getVariantPreview(item);

              const duplicateIndex = item.createNewVariant
                ? findDuplicateVariantIndex(index)
                : -1;

              const hasDuplicateVariant = duplicateIndex !== -1;

              return (
                <Card
                  key={index}
                  variant="outlined"
                  sx={{
                    mb: 2,
                    p: 2,
                  }}
                >
                  {/* Item header */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1.5,
                    }}
                  >
                    <Typography variant="subtitle2" color="primary">
                      {t("purchaseOrders_ext.itemNumber", {
                        number: index + 1,
                      })}
                    </Typography>

                    {!fieldsLocked && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeItem(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>

                  {/* Row 1: Product | Qty */}
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <Autocomplete
                        options={products}
                        disabled={fieldsLocked}
                        getOptionLabel={(o) =>
                          `${o.name}${o.sku ? ` (${o.sku})` : ""}`
                        }
                        value={item.product}
                        onChange={(_, sel) => handleProductSelect(index, sel)}
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            <Box>
                              <Typography variant="body2">
                                {option.name}
                              </Typography>

                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                SKU: {option.sku || "—"}
                              </Typography>
                            </Box>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t("products.product")}
                            required
                            placeholder={t("purchaseOrders_ext.searchProduct")}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label={t("common.qty")}
                        type="number"
                        value={item.quantity}
                        onChange={(e) =>
                          updateItem(index, "quantity", e.target.value)
                        }
                        inputProps={{
                          min: 1,
                        }}
                        disabled={fieldsLocked}
                        required
                      />
                    </Grid>
                  </Grid>

                  {/* Row 2: Cost | Sell | Margin | Total */}
                  <Grid
                    container
                    spacing={2}
                    sx={{
                      mt: 0.5,
                    }}
                  >
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label={t("purchaseOrders.unitCost")}
                        type="number"
                        value={item.unitCost}
                        onChange={(e) =>
                          updateItem(index, "unitCost", e.target.value)
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {currencySymbol}
                            </InputAdornment>
                          ),
                        }}
                        helperText={t("purchaseOrders_ext.unitCostHelper")}
                        disabled={fieldsLocked}
                        required
                      />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label={t("purchaseOrders_ext.sellPrice")}
                        type="number"
                        value={item.sellPrice}
                        onChange={(e) =>
                          updateItem(index, "sellPrice", e.target.value)
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {currencySymbol}
                            </InputAdornment>
                          ),
                        }}
                        helperText={t("purchaseOrders_ext.sellPriceHelper")}
                        disabled={fieldsLocked}
                        required
                      />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label={t("purchaseOrders_ext.margin")}
                        value={fmt(getMargin(item))}
                        disabled
                        InputProps={{
                          sx: {
                            color:
                              getMargin(item) >= 0
                                ? "success.main"
                                : "error.main",
                            fontWeight: "bold",
                          },
                        }}
                        helperText={t("purchaseOrders_ext.marginHelper")}
                      />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label={t("orders.lineTotal")}
                        value={fmt(getLineTotal(item))}
                        disabled
                        helperText={t("purchaseOrders_ext.lineTotalHelper")}
                      />
                    </Grid>
                  </Grid>

                  {/* Variant section */}
                  {item.product && (
                    <>
                      <Divider
                        sx={{
                          my: 2,
                        }}
                      />

                      {/* Existing / New toggle */}
                      {hasExistingVariants && (
                        <RadioGroup
                          row
                          value={item.createNewVariant ? "new" : "existing"}
                          onChange={(e) =>
                            handleVariantModeChange(index, e.target.value)
                          }
                        >
                          <FormControlLabel
                            value="existing"
                            control={<Radio size="small" />}
                            label={t("purchaseOrders_ext.useExisting")}
                            disabled={fieldsLocked}
                          />

                          <FormControlLabel
                            value="new"
                            control={<Radio size="small" />}
                            label={t("purchaseOrders_ext.createNewVariant")}
                            disabled={fieldsLocked}
                          />
                        </RadioGroup>
                      )}

                      {/* Existing variant selector */}
                      {!isCreatingNew && (
                        <FormControl
                          fullWidth
                          disabled={fieldsLocked}
                          sx={{
                            mt: 1,
                          }}
                        >
                          <InputLabel>
                            {t("purchaseOrders_ext.selectVariant")}
                          </InputLabel>

                          <Select
                            value={item.variantId || ""}
                            label={t("purchaseOrders_ext.selectVariant")}
                            onChange={(e) =>
                              updateItem(index, "variantId", e.target.value)
                            }
                          >
                            {itemMeta[index]?.existingVariants?.map((v) => {
                              const alreadyAdded =
                                isExistingVariantOptionDisabled(index, v.id);

                              return (
                                <MenuItem
                                  key={v.id}
                                  value={v.id}
                                  disabled={alreadyAdded}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 2,
                                      width: "100%",
                                    }}
                                  >
                                    <Typography variant="body2">
                                      {v.name}
                                    </Typography>

                                    <Box
                                      sx={{
                                        ml: "auto",
                                        display: "flex",
                                        gap: 2,
                                      }}
                                    >
                                      {alreadyAdded && (
                                        <Chip
                                          label={t(
                                            "purchaseOrders_ext.alreadyAddedChip",
                                          )}
                                          size="small"
                                          color="error"
                                        />
                                      )}

                                      <Chip
                                        label={t(
                                          "purchaseOrders_ext.stockChip",
                                          {
                                            qty: v.stockQuantity,
                                          },
                                        )}
                                        size="small"
                                      />

                                      <Chip
                                        label={t(
                                          "purchaseOrders_ext.sellChip",
                                          {
                                            price: fmt(v.price),
                                          },
                                        )}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                      />

                                      {v.costPrice && (
                                        <Chip
                                          label={t(
                                            "purchaseOrders_ext.costChip",
                                            {
                                              price: fmt(v.costPrice),
                                            },
                                          )}
                                          size="small"
                                          color="secondary"
                                          variant="outlined"
                                        />
                                      )}
                                    </Box>
                                  </Box>
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      )}

                      {/* Duplicate existing-variant warning */}
                      {!isCreatingNew &&
                        findDuplicateExistingVariantIndex(index) !== -1 && (
                          <Alert severity="warning" sx={{ mt: 2 }}>
                            {t(
                              "purchaseOrders_ext.duplicateExistingVariantInline",
                              {
                                number:
                                  findDuplicateExistingVariantIndex(index) + 1,
                              },
                            )}
                          </Alert>
                        )}

                      {/* No variants */}
                      {!hasExistingVariants && !item.createNewVariant && (
                        <Alert
                          severity="info"
                          sx={{
                            mt: 1,
                          }}
                        >
                          {t("purchaseOrders_ext.noVariantsInfo")}
                        </Alert>
                      )}

                      {/* New variant */}
                      {isCreatingNew && (
                        <Box
                          sx={{
                            mt: 2,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            sx={{
                              mb: 1,
                            }}
                          >
                            {t("purchaseOrders_ext.defineAttributes")}
                          </Typography>

                          <Grid container spacing={2}>
                            {storeAttributes.map((attr) => {
                              const availableValues =
                                itemMeta[index]?.availableValuesByAttribute?.[
                                  attr.id
                                ];
                              // undefined = still loading → fall back to all
                              // values rather than flashing an empty list.
                              const options =
                                availableValues !== undefined
                                  ? availableValues
                                  : attr.values || [];
                              const allTaken =
                                availableValues !== undefined &&
                                (attr.values || []).length > 0 &&
                                availableValues.length === 0;

                              return (
                              <Grid item xs={12} md={4} key={attr.id}>
                                <Autocomplete
                                  options={options}
                                  disabled={allTaken || fieldsLocked}
                                  getOptionLabel={(option) =>
                                    option?.displayValue || option?.value || ""
                                  }
                                  value={
                                    (attr.values || []).find((value) =>
                                      (item.attributeValueIds || []).some(
                                        (selectedId) =>
                                          String(selectedId) ===
                                          String(value.id),
                                      ),
                                    ) || null
                                  }
                                  onChange={(_, selectedValue) =>
                                    handleAttributeValueChange(
                                      index,
                                      attr.id,
                                      selectedValue,
                                    )
                                  }
                                  getOptionDisabled={(option) =>
                                    isAttributeOptionDisabled(
                                      index,
                                      attr.id,
                                      option.id,
                                    )
                                  }
                                  isOptionEqualToValue={(option, value) =>
                                    String(option.id) === String(value.id)
                                  }
                                  renderOption={(props, option) => {
                                    const disabled = isAttributeOptionDisabled(
                                      index,
                                      attr.id,
                                      option.id,
                                    );

                                    return (
                                      <li
                                        {...props}
                                        key={option.id}
                                        aria-disabled={disabled}
                                      >
                                        {option?.displayValue ||
                                          option?.value ||
                                          ""}
                                      </li>
                                    );
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label={attr.name}
                                      size="small"
                                      helperText={
                                        allTaken
                                          ? t(
                                              "purchaseOrders_ext.allAttributeValuesTaken",
                                            )
                                          : undefined
                                      }
                                    />
                                  )}
                                />
                              </Grid>
                              );
                            })}
                          </Grid>

                          {/* Duplicate warning */}
                          {hasDuplicateVariant && (
                            <Alert
                              severity="error"
                              sx={{
                                mt: 2,
                              }}
                            >
                              {t("purchaseOrders_ext.duplicateVariantInline", {
                                number: duplicateIndex + 1,
                              })}
                            </Alert>
                          )}

                          {/* Margin preview */}
                          {preview && (
                            <Card
                              sx={{
                                mt: 2,
                                p: 2,
                                bgcolor:
                                  preview.margin >= 0
                                    ? "success.50"
                                    : "error.50",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  mb: 0.5,
                                }}
                              >
                                <TrendingUpIcon
                                  fontSize="small"
                                  color={
                                    preview.margin >= 0 ? "success" : "error"
                                  }
                                />

                                <Typography variant="subtitle2">
                                  {t("purchaseOrders_ext.variantPreview")}
                                </Typography>
                              </Box>

                              <Typography variant="body2">
                                {item.product.name} —{" "}
                                {preview.attrLabels.join(" / ")}
                              </Typography>

                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 3,
                                  mt: 1,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {t("purchaseOrders_ext.sellPriceLabel")}{" "}
                                  <strong>{fmt(preview.sellPrice)}</strong>
                                </Typography>

                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {t("purchaseOrders_ext.costPriceLabel")}{" "}
                                  <strong>{fmt(preview.costPrice)}</strong>
                                </Typography>

                                <Typography
                                  variant="caption"
                                  color={
                                    preview.margin >= 0
                                      ? "success.main"
                                      : "error.main"
                                  }
                                >
                                  {t("purchaseOrders_ext.marginLabel")}{" "}
                                  <strong>{fmt(preview.margin)}</strong>
                                </Typography>
                              </Box>
                            </Card>
                          )}
                        </Box>
                      )}
                    </>
                  )}
                </Card>
              );
            })}

            {/* Grand total */}
            {formData.items.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  mt: 2,
                  pt: 2,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="h6">
                  {t("purchaseOrders_ext.grandTotal", {
                    amount: fmt(getGrandTotal()),
                  })}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Duplicate global warning */}
        {hasDuplicateVariants && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
              {t("purchaseOrders_ext.duplicateVariantsFound")}
            </Typography>

            {formData.items.map((item, index) => {
              const itemHasExistingVariants =
                itemMeta[index]?.existingVariants?.length > 0;
              const itemIsCreatingNew =
                item.createNewVariant || !itemHasExistingVariants;

              const duplicateIndex = itemIsCreatingNew
                ? findDuplicateVariantIndex(index)
                : findDuplicateExistingVariantIndex(index);

              if (duplicateIndex === -1) {
                return null;
              }

              // Avoid displaying the same duplicate twice.
              if (index > duplicateIndex) {
                return null;
              }

              const productName =
                item.product?.name ||
                t("purchaseOrders_ext.itemNumber", { number: index + 1 });

              const duplicateProductName =
                formData.items[duplicateIndex]?.product?.name ||
                t("purchaseOrders_ext.itemNumber", {
                  number: duplicateIndex + 1,
                });

              const variantLabel = itemIsCreatingNew
                ? storeAttributes
                    .flatMap((attr) =>
                      (attr.values || [])
                        .filter((value) =>
                          (item.attributeValueIds || []).some(
                            (selectedId) =>
                              String(selectedId) === String(value.id),
                          ),
                        )
                        .map((value) => value.displayValue || value.value),
                    )
                    .join(" / ")
                : itemMeta[index]?.existingVariants?.find(
                    (v) => String(v.id) === String(item.variantId),
                  )?.name || "";

              return (
                <Box key={`${index}-${duplicateIndex}`} sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    <strong>
                      {t("purchaseOrders_ext.itemLabel", {
                        number: index + 1,
                        name: productName,
                      })}
                    </strong>{" "}
                    {t("purchaseOrders_ext.and")}{" "}
                    <strong>
                      {t("purchaseOrders_ext.itemLabel", {
                        number: duplicateIndex + 1,
                        name: duplicateProductName,
                      })}
                    </strong>{" "}
                    {t("purchaseOrders_ext.haveSameVariant")}
                  </Typography>

                  {variantLabel && (
                    <Chip label={variantLabel} size="small" sx={{ mt: 0.5 }} />
                  )}
                </Box>
              );
            })}

            <Typography variant="body2" sx={{ mt: 1 }}>
              {t("purchaseOrders_ext.chooseDifferentCombination")}
            </Typography>
          </Alert>
        )}

        {/* Submit */}
        {!readOnlyMode && (
          <Box
            sx={{
              display: "flex",
              gap: 2,
            }}
          >
              <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={
                    loading ||
                    (!notesOnlyMode &&
                      (formData.items.length === 0 || hasDuplicateVariants))
                  }
                  sx={{
                      '&.Mui-disabled': {
                          backgroundColor: '#302e2e !important',
                          color: '#ffffff',
                      }
                  }}
              >
                  {loading
                    ? t("common.save") + "..."
                    : isEditMode
                    ? t("common.save")
                    : t("purchaseOrders.createPO")}
              </Button>

            <Button
              variant="outlined"
              onClick={() =>
                navigate(isEditMode ? `/purchase-orders/${id}` : "/purchase-orders")
              }
            >
              {t("common.cancel")}
            </Button>
          </Box>
        )}
      </form>
    </Box>
  );
};

export default PurchaseOrderForm;
